import React, { useEffect, useState } from 'react'
import { gql, useMutation, useQuery } from '@apollo/client'
import { useHistory } from 'react-router-dom'
import {
  Box, CircularProgress, Grid, Tab, Tabs, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material'
import { FEE_QUERY_FIELDS } from '../components/PartnerSignups/FeeFields'
import { Icon, T } from '../components/PartnerSignups/onboardingDesign'
import {
  Badge, Card, CardTitle, Notice, OnboardingPage, PageHeader, PrimaryButton, StatCard,
} from '../components/PartnerSignups/onboardingUi'
import {
  COMMISSION_KIND, ChangeFeesDialog, INVITES, InviteCard, InvitesTable, ReferralsTable, Rule, STANDARD, SignupLinkCard,
  headCell, money, pct, plural, tabSx, tableSx,
} from '../components/Agents/PortalParts'
import {
  BANK_FIELDS, BankFields, ISO_RATE_FIELDS, MONTH_FIELDS, MonthAccounts, PAYOUT_FIELDS, PayoutsTable, PlatformStats, bankToForm,
} from '../components/Agents/isoParts'
import IsoPortal from './IsoPortal'

/**
 * The sales agent's home: how they earn, their sign-up link, a form that
 * emails it to a restaurant, applications they start for a restaurant (filled
 * in on its own page, views/AgentApplication.jsx, with fees a super admin
 * approves before the restaurant is emailed), the restaurants that came in,
 * and what they earned. Everything here is the agent's own data
 * (resolvers/agent.js, utils/merchantInvites.js, utils/agentEarnings.js).
 *
 * An ISO signs in here too and gets its own portal (views/IsoPortal.jsx). An
 * ISO's agent gets this page with their ISO's terms: a split of what the ISO
 * earns on their accounts and a bonus per approved account
 * (resolvers/iso.js), instead of RushMyFood's agent commission.
 * Styled like the merchant sign-up form (onboardingUi).
 */

const PROFILE = gql`
  query MyAgentProfile {
    myAgentProfile {
      _id name email code link commission orderRate extraShare
      stats { invites started signed approved rejected pendingAmount paidAmount }
      thisMonth { label orders subtotal extra amount restaurants { restaurantId restaurantName rate orders subtotal extra amount } }
      network { role isoId isoName split bonus bonusOrders agentsPaidBy rates { ${ISO_RATE_FIELDS} } }
    }
  }
`
// Their commissions: approvals and months of orders (backend utils/agentEarnings.js).
const MY_COMMISSIONS = gql`
  query MyAgentCommissions {
    myAgentCommissions { _id kind restaurantName periodLabel ordersCount subtotal amount status paidAt createdAt }
  }
`
const REFERRALS = gql`
  query MyAgentReferrals {
    myAgentReferrals {
      _id restaurantName contactEmail businessType status startedAt signedAt approvedAt commissionAmount commissionStatus
      pricing held requestNote canChangeFees canOpen
      fees { ${FEE_QUERY_FIELDS} }
    }
  }
`
// An ISO agent's share of the month and their payouts (backend resolvers/iso.js).
const MY_ISO_MONTH = gql`query MyIsoMonthAsAgent { myIsoMonth { ${MONTH_FIELDS} } }`
const MY_ISO_PAYOUTS = gql`query MyIsoPayoutsAsAgent { myIsoPayouts { ${PAYOUT_FIELDS} } }`
const MY_BANK = gql`query MyPayoutBank { myPayoutBank { ${BANK_FIELDS} } }`
const SAVE_BANK = gql`mutation SaveMyPayoutBank($input: PayoutBankInput!) { saveMyPayoutBank(input: $input) { ${BANK_FIELDS} } }`

const APP_PAGE = '/agent/application'

/** The rules, with this agent's own amounts. */
function HowItWorks({ me }) {
  const rate = Number(me.orderRate) || 0
  return (
    <Card>
      <CardTitle sub="The rules for bringing restaurants to RushMyFood and how you are paid.">How it works</CardTitle>
      <Rule icon={Icon.Link} title="1. Bring a restaurant in">
        <li>Share your sign-up link, or send it from this page. Any restaurant or grocery store that signs up through it is credited to you.</li>
        <li>Or start an application yourself: fill in what you know, set the fees, then send it to the restaurant to finish and sign.</li>
      </Rule>
      <Rule icon={Icon.Coins} title="2. What you earn">
        <li><strong>{money(me.commission)}</strong> once, when RushMyFood approves the restaurant.</li>
        <li><strong>{pct(me.extraShare)}</strong> of whatever a restaurant’s fees are above the minimums. Set delivery at 30% against the 20% minimum, and you earn {pct(me.extraShare)} of the extra 10% on its delivery orders.</li>
        {rate > 0
          ? <li><strong>{pct(rate)}</strong> of the subtotal of every online order the restaurant gets, for as long as it stays with RushMyFood.</li>
          : <li>You don’t earn on orders right now.</li>}
      </Rule>
      {rate > 0 && (
        <Rule icon={Icon.Receipt} title="3. Which orders count">
          <li>Online orders: the RushMyFood app and website, and ordering on the restaurant’s own website. Not in-store (POS) sales.</li>
          <li>The subtotal is the price of the items before discounts. Tax, tips, delivery and service fees aren’t included.</li>
          <li>Cancelled orders, orders never paid for, and refunded orders don’t count.</li>
          <li>Each restaurant keeps the rate you had on the day it was approved, even if your rate changes later.</li>
        </Rule>
      )}
      <Rule icon={Icon.Calendar} title={`${rate > 0 ? 4 : 3}. When you are paid`}>
        <li>Your approval amount is added to your commissions the day the restaurant is approved.</li>
        {rate > 0 && <li>Order earnings are added up by month (New York time) and added to your commissions after each month ends.</li>}
        <li>RushMyFood pays your commissions and marks them paid here.</li>
      </Rule>
      <Rule icon={Icon.Pen} title={`${rate > 0 ? 5 : 4}. Fees and signing`}>
        <li>The order percentages, the third-party orders % and the service fee are minimums. Set them higher and they apply right away. Going below a minimum, or changing any other fee, needs a super admin’s approval, and the restaurant can’t sign until it’s approved. You can always send it at the standard fees instead.</li>
        <li>Only the restaurant’s owner can accept the terms and sign the agreement.</li>
      </Rule>
    </Card>
  )
}

/** The rules for one of an ISO's agents: their ISO's split and bonus. */
function IsoAgentHowItWorks({ network }) {
  const iso = network.isoName || 'Your ISO'
  const bonus = Number(network.bonus) || 0
  return (
    <Card>
      <CardTitle sub={`You work with ${iso}. These are the terms ${iso} set for you.`}>How it works</CardTitle>
      <Rule icon={Icon.Link} title="1. Bring a restaurant in">
        <li>Share your sign-up link, or send it from this page. Any restaurant or grocery store that signs up through it is credited to you, and to {iso}.</li>
        <li>Or start an application yourself: fill in what you know, set the fees, then send it to the restaurant to finish and sign.</li>
      </Rule>
      <Rule icon={Icon.Coins} title="2. What you earn">
        <li><strong>{pct(network.split)}</strong> of what {iso} earns on every account you bring in, for as long as it stays with RushMyFood.</li>
        {bonus > 0 && <li><strong>{money(bonus)}</strong> once for each account, when it reaches <strong>{network.bonusOrders || 50} orders</strong> (on every platform, counted from the day it’s approved).</li>}
        <li>When a restaurant’s fees are above the minimums, {iso} earns a share of the difference, and your split includes it.</li>
        <li>Each account keeps the split and bonus you had on the day it was approved.</li>
      </Rule>
      <Rule icon={Icon.Receipt} title="3. Which orders count">
        <li>Orders on RushMyFood (the app, the website and the restaurant’s own website), in-store orders on the RushMyFood POS, and DoorDash, Uber Eats and Grubhub orders once RushMyFood manages them.</li>
        <li>Cancelled orders, orders never paid for, and refunded orders don’t count.</li>
      </Rule>
      <Rule icon={Icon.Calendar} title="4. When you are paid">
        <li>Earnings are added up by month (New York time) after each month ends.</li>
        <li>{network.agentsPaidBy === 'RUSHMYFOOD'
          ? `${iso} has RushMyFood pay you directly. Add your bank details on this page.`
          : `${iso} pays you. You can see here what you earned and when ${iso} marks it paid.`}
        </li>
      </Rule>
      <Rule icon={Icon.Pen} title="5. Fees and signing">
        <li>The order percentages, the third-party orders % and the service fee are minimums. Set them higher and they apply right away. Going below a minimum, or changing any other fee, needs a super admin’s approval, and the restaurant can’t sign until it’s approved. You can always send it at the standard fees instead.</li>
        <li>Only the restaurant’s owner can accept the terms and sign the agreement.</li>
      </Rule>
    </Card>
  )
}

/** An ISO agent's bank details, for an ISO that has RushMyFood pay its agents. */
function PayoutBankCard({ network }) {
  const { data } = useQuery(MY_BANK, { fetchPolicy: 'network-only' })
  const [save, { loading: saving }] = useMutation(SAVE_BANK)
  const [form, setForm] = useState(null)
  const [notice, setNotice] = useState(null)
  const [error, setError] = useState(null)
  const bank = data && data.myPayoutBank
  useEffect(() => { if (bank && !form) setForm(bankToForm(bank)) }, [bank, form])
  if (!form) return null
  const direct = network.agentsPaidBy === 'RUSHMYFOOD'

  const submit = async () => {
    setNotice(null)
    setError(null)
    try {
      const res = await save({ variables: { input: form } })
      setForm(bankToForm(res.data.saveMyPayoutBank))
      setNotice('Saved.')
    } catch (e) {
      setError(e.message)
    }
  }

  return (
    <Card>
      <CardTitle
        sub={direct
          ? `${network.isoName} has RushMyFood pay you directly. We deposit your earnings here.`
          : `${network.isoName} pays you itself for now. Add these in case it has RushMyFood pay you directly.`}
        action={bank && bank.isSet ? <Badge kind="green">Saved</Badge> : direct ? <Badge kind="amber">Needed</Badge> : null}
      >
        Where we pay you
      </CardTitle>
      {notice && <Notice severity="success" onClose={() => setNotice(null)}>{notice}</Notice>}
      {error && <Notice severity="error" onClose={() => setError(null)}>{error}</Notice>}
      <BankFields value={form} onChange={setForm} idPrefix="my-bank" />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mt: 2, flexWrap: 'wrap' }}>
        <Typography sx={{ color: T.hint, fontSize: 13 }}>Encrypted and stored securely.</Typography>
        <PrimaryButton onClick={submit} busy={saving}>Save bank details</PrimaryButton>
      </Box>
    </Card>
  )
}

/** An ISO agent's share of this month, by account, and what they were paid. */
function IsoAgentEarningsCard({ network, month, payouts }) {
  if (!month || (month.accounts.length === 0 && payouts.length === 0)) return null
  return (
    <Card sx={{ mb: 3, p: 0, overflow: 'hidden' }}>
      <Box sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 3 } }}>
        <CardTitle sub={`${month.label || 'This month'} so far: ${money(month.agentShares)} from ${plural(month.orders, 'order')} on your accounts. Each month is added below when it ends.`}>
          Your share of orders
        </CardTitle>
      </Box>
      {month.platforms && month.platforms.length > 0 && (
        <Box sx={{ overflowX: 'auto', mb: 1 }}>
          <PlatformStats month={month} withEarned={false} />
        </Box>
      )}
      {month.accounts.length > 0 && (
        <Box sx={{ overflowX: 'auto' }}>
          <MonthAccounts accounts={month.accounts} forAgent isoName={network.isoName} />
        </Box>
      )}
      {payouts.length > 0 && (
        <Box sx={{ borderTop: `1px solid ${T.divider}` }}>
          <Typography sx={{ fontWeight: 700, fontSize: 15, px: { xs: 2, sm: 3 }, pt: 2, pb: 1 }}>What you earned</Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <PayoutsTable payouts={payouts} />
          </Box>
        </Box>
      )}
    </Card>
  )
}

export default function AgentPortal() {
  const { data: p, loading, error } = useQuery(PROFILE, { fetchPolicy: 'network-only' })
  if (loading) return <OnboardingPage><Box sx={{ py: 10, textAlign: 'center' }}><CircularProgress sx={{ color: T.red }} /></Box></OnboardingPage>
  if (error) return <OnboardingPage><Notice severity="error">{error.message}</Notice></OnboardingPage>
  const me = p && p.myAgentProfile
  if (!me) return null
  if (me.network && me.network.role === 'ISO') return <IsoPortal me={me} />
  return <AgentHome me={me} />
}

function AgentHome({ me }) {
  const history = useHistory()
  const isoAgent = me.network && me.network.role === 'ISO_AGENT' ? me.network : null
  const { data: r, refetch: refetchReferrals } = useQuery(REFERRALS, { fetchPolicy: 'network-only' })
  const { data: i, refetch: refetchInvites } = useQuery(INVITES, { fetchPolicy: 'network-only' })
  const { data: mc } = useQuery(MY_COMMISSIONS, { fetchPolicy: 'network-only', skip: !!isoAgent })
  const { data: im } = useQuery(MY_ISO_MONTH, { fetchPolicy: 'network-only', skip: !isoAgent })
  const { data: ip } = useQuery(MY_ISO_PAYOUTS, { fetchPolicy: 'network-only', skip: !isoAgent })
  const [tab, setTab] = useState(0)
  const [changing, setChanging] = useState(null)
  const [appNotice, setAppNotice] = useState(null)
  // The same sign-up form restaurants use, on its own page here: a new
  // application, or one of theirs.
  const openApplication = (id) => history.push(`${APP_PAGE}/${id || 'new'}`)
  const { data: std } = useQuery(STANDARD, { fetchPolicy: 'network-only' })
  const standard = std && std.merchantAgreementFees

  const s = me.stats || {}
  const referrals = (r && r.myAgentReferrals) || []
  const invites = (i && i.myAgentInvites) || []
  const month = me.thisMonth || { orders: 0, subtotal: 0, amount: 0, restaurants: [] }
  const statements = ((mc && mc.myAgentCommissions) || []).filter(c => c.kind === 'ORDERS')
  const isoMonth = im && im.myIsoMonth
  const isoPayouts = (ip && ip.myIsoPayouts) || []

  return (
    <OnboardingPage>
      <PageHeader
        eyebrow={isoAgent ? `Agent portal · ${isoAgent.isoName}` : 'Agent portal'}
        title={`Hi ${String(me.name || '').split(' ')[0]}`}
        subtitle="Bring restaurants and grocery stores to RushMyFood, and earn on every one."
        actions={(
          <PrimaryButton onClick={() => openApplication(null)}>
            <Icon.Plus size={18} />
            Start an application
          </PrimaryButton>
        )}
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))', lg: 'repeat(6, minmax(0, 1fr))' }, gap: 2, mb: 3 }}>
        <StatCard label="Invited" value={s.invites || 0} />
        <StatCard label="Started" value={s.started || 0} />
        <StatCard label="Approved" value={s.approved || 0} tone="green" />
        <StatCard label="On orders this month" value={money(isoAgent ? isoMonth && isoMonth.agentShares : month.amount)} />
        <StatCard label="Earned, pending" value={money(s.pendingAmount)} tone="red" />
        <StatCard label="Paid to you" value={money(s.paidAmount)} tone="green" />
      </Box>

      {appNotice && <Notice severity="success" onClose={() => setAppNotice(null)}>{appNotice}</Notice>}

      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} md={7}>
          {isoAgent ? <IsoAgentHowItWorks network={isoAgent} /> : <HowItWorks me={me} />}
        </Grid>
        <Grid item xs={12} md={5}>
          <Box sx={{ display: 'grid', gap: 2.5 }}>
            <SignupLinkCard link={me.link} code={me.code} />
            <InviteCard onSent={() => { refetchInvites(); refetchReferrals() }} />
            {isoAgent && <PayoutBankCard network={isoAgent} />}
          </Box>
        </Grid>
      </Grid>

      {isoAgent && <IsoAgentEarningsCard network={isoAgent} month={isoMonth} payouts={isoPayouts} />}

      {!isoAgent && (month.restaurants.length > 0 || statements.length > 0) && (
        <Card sx={{ mb: 3, p: 0, overflow: 'hidden' }}>
          <Box sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 3 } }}>
            <CardTitle sub={`${month.label || 'This month'} so far: ${money(month.amount)} from ${plural(month.orders, 'order')} (${money(month.subtotal)} subtotal). Each month is added to your commissions when it ends.`}>
              Commission on orders
            </CardTitle>
          </Box>
          {month.restaurants.length > 0 && (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 520, ...tableSx }}>
                <TableHead>
                  <TableRow>
                    {['Restaurant', 'Your rate', 'Orders', 'Subtotal', 'You earn'].map(h => (
                      <TableCell key={h} sx={headCell} align={h === 'You earn' ? 'right' : 'left'}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {month.restaurants.map(x => (
                    <TableRow key={x.restaurantId}>
                      <TableCell sx={{ fontWeight: 600 }}>{x.restaurantName || `Restaurant #${x.restaurantId}`}</TableCell>
                      <TableCell>{pct(x.rate)}</TableCell>
                      <TableCell>{x.orders}</TableCell>
                      <TableCell>{money(x.subtotal)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {money(x.amount)}
                        {x.extra > 0 && <Typography sx={{ color: T.hint, fontSize: 12.5, fontWeight: 400 }}>incl. {money(x.extra)} above the minimums</Typography>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
          {statements.length > 0 && (
            <Box sx={{ px: { xs: 2, sm: 3 }, py: 2, borderTop: `1px solid ${T.divider}` }}>
              <Typography sx={{ fontWeight: 700, fontSize: 15, mb: 0.75 }}>Past months</Typography>
              {statements.map(c => {
                const [label, kind] = COMMISSION_KIND[c.status] || COMMISSION_KIND.PENDING
                return (
                  <Box key={c._id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, py: 1, borderTop: `1px solid ${T.divider}`, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: 14.5, color: T.text }}>{c.periodLabel}: {plural(c.ordersCount, 'order')}, {money(c.subtotal)} subtotal</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: 14.5 }}>{money(c.amount)}</Typography>
                      <Badge kind={kind}>{label}</Badge>
                    </Box>
                  </Box>
                )
              })}
            </Box>
          )}
        </Card>
      )}

      <Card sx={{ p: 0, overflow: 'hidden' }}>
        <Tabs
          value={tab} onChange={(e, v) => setTab(v)}
          sx={{ borderBottom: `1px solid ${T.divider}`, px: { xs: 1, sm: 2 }, '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' } }}
        >
          <Tab label={`My restaurants (${referrals.length})`} sx={tabSx} />
          <Tab label={`Invitations sent (${invites.length})`} sx={tabSx} />
        </Tabs>
        <Box sx={{ overflowX: 'auto' }}>
          {tab === 0 ? (
            <ReferralsTable
              referrals={referrals} standard={standard} onOpen={openApplication} onChangeFees={setChanging}
              commissionHeader={isoAgent ? 'Bonus' : 'Commission'}
            />
          ) : (
            <InvitesTable invites={invites} />
          )}
        </Box>
      </Card>

      {changing && standard && (
        <ChangeFeesDialog
          referral={changing}
          standard={standard}
          extraText={isoAgent
            ? `${isoAgent.isoName} earns its share of whatever is above the minimums, and you get your ${pct(isoAgent.split)} of it.`
            : `You earn ${pct(me.extraShare)} of whatever is above the minimums, on top of your ${pct(me.orderRate)} of every online order.`}
          onClose={() => setChanging(null)}
          onDone={(message) => { setChanging(null); setAppNotice(message); refetchReferrals() }}
        />
      )}
    </OnboardingPage>
  )
}
